// ═══════════════════════════════════════════════════════════════
// GiftsSection.jsx
// Collapsible gift history table for DonorDetailView main column.
// Loads gifts ON DEMAND when expanded — zero cost when collapsed.
// Receipt generation is Phase 4; receipt button is placeholder here.
// Path: src/components/features/GiftsSection.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import {
  Gift,
  Plus,
  Printer,
  Pencil,
  Trash2,
  ChevronDown,
  AlertCircle,
  Check,
  Clock,
  ArrowRightLeft,
  Search,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import {
  formatCurrency,
  getGiftTypeLabel,
  getPaymentMethodLabel,
} from "../../constants/giftTypes";
import { giftsService } from "../../services/giftsService";
import { GiftEntryForm } from "./GiftEntryForm";
import { useApp } from "../../context/AppContext";

export const GiftsSection = ({ member, palette, canEdit, onGiftSaved }) => {
  const { activeFunds, chapterSettings, showMessage, members } = useApp();

  // ── Collapse state — starts closed, loads on first open ──────
  const [collapsed, setCollapsed] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  // ── Data state ────────────────────────────────────────────────
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── UI state ──────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGift, setEditingGift] = useState(null); // gift object or null
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reassignGiftId, setReassignGiftId] = useState(null);
  const [reassignSearch, setReassignSearch] = useState("");
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassigning, setReassigning] = useState(false);

  // ── Reset and reload when member changes ───────────────────────
  useEffect(() => {
    let cancelled = false;
    setGifts([]);
    setHasLoaded(false);
    setShowAddForm(false);
    setEditingGift(null);
    // If section is already open, reload immediately for new member
    if (!collapsed && member?.id) {
      setLoading(true);
      setError(null);
      giftsService
        .getByMember(member.id)
        .then((data) => {
          if (cancelled) return;
          setGifts(data);
          setHasLoaded(true);
        })
        .catch((err) => {
          if (cancelled) return;
          setError("Could not load gifts — " + err.message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => { cancelled = true; };
  }, [member?.id, collapsed]);

  // ── Load gifts when section first opens ───────────────────────
  const loadGifts = useCallback(async () => {
    if (!member?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await giftsService.getByMember(member.id);
      setGifts(data);
      setHasLoaded(true);
    } catch (err) {
      setError("Could not load gifts — " + err.message);
    } finally {
      setLoading(false);
    }
  }, [member?.id]);

  const handleToggle = () => {
    const opening = collapsed;
    setCollapsed((prev) => !prev);
    if (opening && !hasLoaded) loadGifts();
  };

  // Reload after save / delete
  const handleGiftSaved = () => {
    setShowAddForm(false);
    setEditingGift(null);
    loadGifts();
    onGiftSaved?.(); // refresh donor summary fields in AppContext
  };

  // ── Delete gift ───────────────────────────────────────────────
  const handleDelete = async (giftId) => {
    setDeleting(true);
    try {
      await giftsService.remove(giftId);
      showMessage("Gift deleted", "warning");
      setConfirmDeleteId(null);
      loadGifts();
      onGiftSaved?.();
    } catch (err) {
      showMessage("Error deleting gift — " + err.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  // ── Reassign gift ─────────────────────────────────────────────
  const handleReassign = async () => {
    if (!reassignGiftId || !reassignTarget) return;
    setReassigning(true);
    try {
      await giftsService.update(reassignGiftId, { memberId: reassignTarget.id });
      const targetName = [reassignTarget.firstName, reassignTarget.lastName].filter(Boolean).join(" ") || reassignTarget.organization || "donor";
      showMessage(`Gift reassigned to ${targetName}`);
      setReassignGiftId(null);
      setReassignSearch("");
      setReassignTarget(null);
      loadGifts();
      onGiftSaved?.();
    } catch (err) {
      showMessage("Error reassigning gift — " + err.message, "error");
    } finally {
      setReassigning(false);
    }
  };

  // ── Format date ───────────────────────────────────────────────
  const fmt = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ── Acknowledgment badge ──────────────────────────────────────
  const AckBadge = ({ gift }) => {
    if (gift.acknowledgmentSent) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            fontSize: 10,
            fontWeight: 600,
            color: "#16a34a",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          <Check size={ICON_SIZES.xs} /> Sent
        </span>
      );
    }
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          fontSize: 10,
          fontWeight: 600,
          color: "#d97706",
          background: "#fffbeb",
          border: "1px solid #fde68a",
          padding: "2px 6px",
          borderRadius: 4,
        }}
      >
        <Clock size={ICON_SIZES.xs} /> Pending
      </span>
    );
  };

  // ── Gift type color dot ───────────────────────────────────────
  const typeColors = {
    cash: "#3b82f6",
    inkind: "#8b5cf6",
    stock: "#10b981",
    daf: "#f59e0b",
  };

  // ── Section header bar (matches SectionHead pattern) ─────────
  const headerBg = palette.accent1;

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Header — same style as SectionHead in DonorDetailView */}
      <div
        onClick={handleToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          marginBottom: collapsed ? 0 : 12,
          userSelect: "none",
          background: headerBg,
          borderRadius: collapsed ? 8 : "8px 8px 0 0",
          transition: "all 0.15s ease",
          boxShadow: `0 2px 8px ${headerBg}30`,
          cursor: "pointer",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
      >
        <Gift size={ICON_SIZES.md} style={{ color: "#ffffff", flexShrink: 0 }} />
        <span
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#ffffff",
            fontFamily: FONT_FAMILY,
            flex: 1,
          }}
        >
          Gift History
          {member?.giftCount > 0 && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 600,
                background: "rgba(255,255,255,0.25)",
                padding: "1px 7px",
                borderRadius: 999,
              }}
            >
              {member.giftCount}
            </span>
          )}
        </span>
        {/* Add gift button in header (stops propagation) */}
        {canEdit && !collapsed && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingGift(null);
              setShowAddForm(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 10px",
              borderRadius: 6,
              border: "none",
              background: "rgba(255,255,255,0.2)",
              color: "#ffffff",
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.35)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <Plus size={ICON_SIZES.sm} /> Add Gift
          </button>
        )}
        <ChevronDown
          size={ICON_SIZES.sm}
          style={{
            color: "rgba(255,255,255,0.7)",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        />
      </div>

      {/* Content */}
      {!collapsed && (
        <div
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderTop: "none",
            borderRadius: "0 0 8px 8px",
            overflow: "hidden",
          }}
        >
          {/* Loading */}
          {loading && (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: palette.text3,
                fontSize: FONT_SIZES.sm,
              }}
            >
              Loading gifts...
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "14px 16px",
                background: "#fef2f2",
                borderBottom: `1px solid ${palette.cardBorder}`,
                fontSize: FONT_SIZES.sm,
                color: "#991b1b",
              }}
            >
              <AlertCircle size={ICON_SIZES.sm} />
              {error}
            </div>
          )}

          {/* Add / Edit form */}
          {(showAddForm || editingGift) && (
            <div
              style={{
                padding: "16px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                background: `${palette.accent1}04`,
              }}
            >
              <div
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  color: palette.accent1,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 12,
                }}
              >
                {editingGift ? "Edit Gift" : "New Gift"}
              </div>
              <GiftEntryForm
                member={member}
                gift={editingGift}
                onSaved={handleGiftSaved}
                onCancel={() => {
                  setShowAddForm(false);
                  setEditingGift(null);
                }}
                palette={palette}
                activeFunds={activeFunds}
                chapterSettings={chapterSettings}
                showTypeSelector={true}
              />
            </div>
          )}

          {/* Gift table */}
          {!loading && !error && gifts.length === 0 && !showAddForm && (
            <div
              style={{
                padding: "28px 20px",
                textAlign: "center",
              }}
            >
              <Gift
                size={ICON_SIZES.xl}
                style={{ color: palette.text3, marginBottom: 8 }}
              />
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  color: palette.text3,
                  marginBottom: canEdit ? 12 : 0,
                }}
              >
                No gifts recorded yet.
              </div>
              {canEdit && (
                <button
                  onClick={() => setShowAddForm(true)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 16px",
                    borderRadius: 8,
                    border: `1px solid ${palette.accent1}`,
                    background: `${palette.accent1}10`,
                    color: palette.accent1,
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 700,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                  }}
                >
                  <Plus size={ICON_SIZES.sm} /> Record First Gift
                </button>
              )}
            </div>
          )}

          {!loading && !error && gifts.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: `${palette.accent1}08`,
                      borderBottom: `2px solid ${palette.cardBorder}`,
                    }}
                  >
                    {[
                      "Date",
                      "Type",
                      "Gross",
                      "G&S FMV",
                      "Net Deductible",
                      "Fund",
                      "Ack",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "8px 12px",
                          textAlign: h === "" ? "right" : "left",
                          fontWeight: 700,
                          color: palette.text2,
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gifts.map((g, idx) => (
                    <React.Fragment key={g.id}>
                      <tr
                        style={{
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          background:
                            idx % 2 === 0 ? "#ffffff" : `${palette.accent1}03`,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = `${palette.accent1}07`)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            idx % 2 === 0 ? "#ffffff" : `${palette.accent1}03`)
                        }
                      >
                        {/* Date */}
                        <td
                          style={{
                            padding: "10px 12px",
                            color: palette.text1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmt(g.giftDate)}
                        </td>

                        {/* Type */}
                        <td
                          style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background:
                                  typeColors[g.giftType] || palette.accent1,
                                flexShrink: 0,
                              }}
                            />
                            {getGiftTypeLabel(g.giftType, true)}
                          </span>
                        </td>

                        {/* Gross */}
                        <td
                          style={{
                            padding: "10px 12px",
                            fontWeight: 600,
                            color: palette.text1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.amount != null ? formatCurrency(g.amount) : "—"}
                        </td>

                        {/* G&S FMV */}
                        <td
                          style={{
                            padding: "10px 12px",
                            color: palette.text2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.fairMarketValue > 0
                            ? formatCurrency(g.fairMarketValue)
                            : "—"}
                        </td>

                        {/* Net Deductible */}
                        <td
                          style={{
                            padding: "10px 12px",
                            color: palette.text2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.deductibleAmount != null
                            ? formatCurrency(g.deductibleAmount)
                            : "—"}
                        </td>

                        {/* Fund */}
                        <td
                          style={{
                            padding: "10px 12px",
                            color: palette.text2,
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.fund?.name || "—"}
                        </td>

                        {/* Ack status */}
                        <td
                          style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                        >
                          <AckBadge gift={g} />
                        </td>

                        {/* Actions */}
                        <td
                          style={{
                            padding: "10px 12px",
                            whiteSpace: "nowrap",
                            textAlign: "right",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              justifyContent: "flex-end",
                            }}
                          >
                            {/* Receipt — Phase 4 placeholder */}
                            <button
                              title="Print receipt (coming in Phase 4)"
                              disabled
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                border: `1px solid ${palette.cardBorder}`,
                                background: "transparent",
                                color: palette.text3,
                                cursor: "not-allowed",
                                opacity: 0.4,
                              }}
                            >
                              <Printer size={ICON_SIZES.sm} />
                            </button>

                            {/* Reassign */}
                            {canEdit && (
                              <button
                                onClick={() => {
                                  setReassignGiftId(g.id);
                                  setReassignSearch("");
                                  setReassignTarget(null);
                                  setConfirmDeleteId(null);
                                }}
                                title="Reassign to another donor"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: "transparent",
                                  color: palette.text2,
                                  cursor: "pointer",
                                  transition: "all 0.12s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f0fdf4";
                                  e.currentTarget.style.borderColor = "#10b981";
                                  e.currentTarget.style.color = "#10b981";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.borderColor = palette.cardBorder;
                                  e.currentTarget.style.color = palette.text2;
                                }}
                              >
                                <ArrowRightLeft size={ICON_SIZES.sm} />
                              </button>
                            )}

                            {/* Edit */}
                            {canEdit && (
                              <button
                                onClick={() => {
                                  setShowAddForm(false);
                                  setEditingGift(g);
                                }}
                                title="Edit gift"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: "transparent",
                                  color: palette.text2,
                                  cursor: "pointer",
                                  transition: "all 0.12s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = `${palette.accent1}12`;
                                  e.currentTarget.style.borderColor =
                                    palette.accent1;
                                  e.currentTarget.style.color = palette.accent1;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    "transparent";
                                  e.currentTarget.style.borderColor =
                                    palette.cardBorder;
                                  e.currentTarget.style.color = palette.text2;
                                }}
                              >
                                <Pencil size={ICON_SIZES.sm} />
                              </button>
                            )}

                            {/* Delete */}
                            {canEdit && (
                              <button
                                onClick={() => setConfirmDeleteId(g.id)}
                                title="Delete gift"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: "transparent",
                                  color: palette.text3,
                                  cursor: "pointer",
                                  transition: "all 0.12s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#fef2f2";
                                  e.currentTarget.style.borderColor = "#ef4444";
                                  e.currentTarget.style.color = "#ef4444";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    "transparent";
                                  e.currentTarget.style.borderColor =
                                    palette.cardBorder;
                                  e.currentTarget.style.color = palette.text3;
                                }}
                              >
                                <Trash2 size={ICON_SIZES.sm} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Inline reassign */}
                      {reassignGiftId === g.id && (
                        <tr
                          style={{
                            background: "#f0fdf4",
                            borderBottom: "1px solid #bbf7d0",
                          }}
                        >
                          <td colSpan={8} style={{ padding: "10px 16px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <ArrowRightLeft
                                size={ICON_SIZES.sm}
                                style={{ color: "#10b981", flexShrink: 0 }}
                              />
                              <span
                                style={{
                                  fontSize: FONT_SIZES.xs,
                                  color: "#166534",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Reassign to:
                              </span>
                              <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
                                <Search
                                  size={ICON_SIZES.xs}
                                  style={{
                                    position: "absolute",
                                    left: 8,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: palette.text3,
                                    pointerEvents: "none",
                                  }}
                                />
                                <input
                                  value={reassignSearch}
                                  onChange={(e) => {
                                    setReassignSearch(e.target.value);
                                    setReassignTarget(null);
                                  }}
                                  placeholder="Search donor name..."
                                  autoFocus
                                  style={{
                                    width: "100%",
                                    padding: "6px 10px 6px 28px",
                                    borderRadius: 6,
                                    fontSize: FONT_SIZES.xs,
                                    fontFamily: FONT_FAMILY,
                                    border: "1px solid #bbf7d0",
                                    background: "#fff",
                                    color: palette.inputColor,
                                    outline: "none",
                                  }}
                                />
                                {reassignSearch.trim().length >= 2 && !reassignTarget && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      marginTop: 2,
                                      background: palette.cardBg,
                                      border: `1px solid ${palette.cardBorder}`,
                                      borderRadius: 6,
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                      zIndex: 20,
                                      maxHeight: 160,
                                      overflowY: "auto",
                                    }}
                                  >
                                    {(members || [])
                                      .filter((m) => {
                                        if (m.id === member?.id) return false;
                                        const query = reassignSearch.toLowerCase();
                                        return `${m.firstName} ${m.lastName} ${m.organization}`
                                          .toLowerCase()
                                          .includes(query);
                                      })
                                      .slice(0, 8)
                                      .map((m) => (
                                        <div
                                          key={m.id}
                                          onClick={() => {
                                            const displayName = [m.firstName, m.lastName].filter(Boolean).join(" ") || m.organization || "Unknown";
                                            setReassignTarget(m);
                                            setReassignSearch(displayName);
                                          }}
                                          style={{
                                            padding: "6px 10px",
                                            fontSize: FONT_SIZES.xs,
                                            fontFamily: FONT_FAMILY,
                                            color: palette.text1,
                                            cursor: "pointer",
                                            borderBottom: `1px solid ${palette.cardBorder}`,
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.background = `${palette.accent1}08`)
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.background = "transparent")
                                          }
                                        >
                                          <strong>
                                            {m.lastName && m.firstName
                                              ? `${m.lastName}, ${m.firstName}`
                                              : m.lastName || m.firstName || m.organization || "Unknown"}
                                          </strong>
                                          {m.organization && m.lastName && (
                                            <span style={{ color: palette.text3, marginLeft: 6 }}>
                                              {m.organization}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={handleReassign}
                                disabled={!reassignTarget || reassigning}
                                style={{
                                  padding: "5px 14px",
                                  borderRadius: 6,
                                  border: "none",
                                  background: reassignTarget ? "#10b981" : "#d1d5db",
                                  color: "#ffffff",
                                  fontSize: FONT_SIZES.xs,
                                  fontWeight: 700,
                                  fontFamily: FONT_FAMILY,
                                  cursor: reassignTarget ? "pointer" : "default",
                                  opacity: reassigning ? 0.6 : 1,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {reassigning ? "Moving..." : "Reassign"}
                              </button>
                              <button
                                onClick={() => {
                                  setReassignGiftId(null);
                                  setReassignSearch("");
                                  setReassignTarget(null);
                                }}
                                style={{
                                  padding: "5px 12px",
                                  borderRadius: 6,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: "transparent",
                                  color: palette.text2,
                                  fontSize: FONT_SIZES.xs,
                                  fontWeight: 600,
                                  fontFamily: FONT_FAMILY,
                                  cursor: "pointer",
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Inline delete confirm */}
                      {confirmDeleteId === g.id && (
                        <tr
                          style={{
                            background: "#fef2f2",
                            borderBottom: `1px solid #fecaca`,
                          }}
                        >
                          <td colSpan={8} style={{ padding: "10px 16px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              <AlertCircle
                                size={ICON_SIZES.sm}
                                style={{ color: "#ef4444", flexShrink: 0 }}
                              />
                              <span
                                style={{
                                  fontSize: FONT_SIZES.xs,
                                  color: "#991b1b",
                                  flex: 1,
                                }}
                              >
                                Delete this gift? This will update the donor
                                summary and cannot be undone.
                              </span>
                              <button
                                onClick={() => handleDelete(g.id)}
                                disabled={deleting}
                                style={{
                                  padding: "5px 14px",
                                  borderRadius: 6,
                                  border: "none",
                                  background: "#ef4444",
                                  color: "#ffffff",
                                  fontSize: FONT_SIZES.xs,
                                  fontWeight: 700,
                                  fontFamily: FONT_FAMILY,
                                  cursor: deleting ? "default" : "pointer",
                                  opacity: deleting ? 0.6 : 1,
                                }}
                              >
                                {deleting ? "Deleting..." : "Delete"}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                style={{
                                  padding: "5px 12px",
                                  borderRadius: 6,
                                  border: `1px solid ${palette.cardBorder}`,
                                  background: "transparent",
                                  color: palette.text2,
                                  fontSize: FONT_SIZES.xs,
                                  fontWeight: 600,
                                  fontFamily: FONT_FAMILY,
                                  cursor: "pointer",
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>

                {/* Totals footer */}
                {gifts.some((g) => g.amount != null) && (
                  <tfoot>
                    <tr
                      style={{
                        borderTop: `2px solid ${palette.cardBorder}`,
                        background: `${palette.accent1}06`,
                      }}
                    >
                      <td
                        colSpan={2}
                        style={{
                          padding: "8px 12px",
                          fontSize: FONT_SIZES.xs,
                          fontWeight: 700,
                          color: palette.text2,
                          textTransform: "uppercase",
                        }}
                      >
                        Totals ({gifts.length} gift
                        {gifts.length !== 1 ? "s" : ""})
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontWeight: 700,
                          color: palette.accent1,
                          fontSize: FONT_SIZES.sm,
                        }}
                      >
                        {formatCurrency(
                          gifts.reduce((s, g) => s + (g.amount || 0), 0)
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          color: palette.text2,
                          fontSize: FONT_SIZES.sm,
                        }}
                      >
                        {gifts.some((g) => g.fairMarketValue > 0)
                          ? formatCurrency(
                              gifts.reduce(
                                (s, g) => s + (g.fairMarketValue || 0),
                                0
                              )
                            )
                          : "—"}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          color: palette.text2,
                          fontSize: FONT_SIZES.sm,
                        }}
                      >
                        {formatCurrency(
                          gifts.reduce(
                            (s, g) => s + (g.deductibleAmount || g.amount || 0),
                            0
                          )
                        )}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
